import { useEffect, useState } from "react";
import {
  useAppDesignPdf,
  useAssignedClientSelection,
  useBusyAction,
  useFetch,
  useRequest,
} from "../../../hooks";
import { DownloadAction } from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import { WellProps, DocRow } from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";

interface WellsFetchResponse {
  success?: boolean;
  data: WellProps[];
}

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

const latestUploadByTallyId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    const tid = (row as any).tallyId as number | undefined;
    if (!tid) return;
    const cur = map.get(tid);
    if (!cur || new Date(row.date).getTime() > new Date(cur.date).getTime()) {
      map.set(tid, row);
    }
  });
  return map;
};

export const TallyDocClient = () => {
  const { handleRequest } = useRequest();
  const { downloadTallyPdf } = useAppDesignPdf();
  const {
    clientOptions,
    selectedClientId,
    setSelectedClientId,
    showClientFilter,
  } = useAssignedClientSelection();
  const { paginationModel, setPaginationModel } = usePaginator("tally-doc-client", 8);

  const { data: wellsResp, isLoading: wellsLoading } =
    useFetch<WellsFetchResponse>(
      selectedClientId ? `well/client/${selectedClientId}` : undefined,
    );
  const wells: WellProps[] = wellsResp?.data ?? [];

  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [source, setSource] = useState(""); // "" = all
  const [owner, setOwner] = useState(""); // "" = all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const getWellName = (idWell?: number) =>
    wells.find((w) => w.id === idWell)?.name ?? "";

  useEffect(() => {
    if (!selectedClientId) return;
    setSelectedWellId(null);
    setRowsLoading(true);
    const opts: RequestInit = { method: "GET" };
    Promise.all([
      handleRequest({ endpoint: `tally-list/client/${selectedClientId}`, options: opts }),
      handleRequest({ endpoint: `tally-detail?client=${selectedClientId}`, options: opts }),
    ])
      .then(([uploadedRes, appRes]) => {
        const uploaded: DocRow[] = (
          isSuccessful(uploadedRes) ? uploadedRes.data : []
        ).map((item: any) => ({
          rowId: `u-${item.id}`,
          customName: item.customName,
          date: item.date ?? "",
          wellId: item.idWell,
          wellName: getWellName(item.idWell),
          source: "uploaded" as const,
          path: item.path,
          owner: item.user?.name ?? "—",
          tallyId: item.tallyId,
        }));
        const uploadsByTally = latestUploadByTallyId(uploaded);
        const app: DocRow[] = getRows(appRes).map((item: any) => {
          const def = uploadsByTally.get(item.id);
          return {
            rowId: `t-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: item.user?.name ?? "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        });
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!wells.length || !rows.length) return;
    setRows((prev) =>
      prev.map((r) => (r.wellName ? r : { ...r, wellName: getWellName(r.wellId) })),
    );
  }, [wells]);

  const downloadUploaded = (path: string, name: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const opts: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key: path }),
        headers: { responseType: "Buffer" } as any,
      };
      handleRequest({ endpoint: "pdf-tally-adm", options: opts })
        .then((res) => {
          if (!res.success) { reject(new Error("Failed")); return; }
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          resolve();
        })
        .catch(reject);
    });

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      await downloadUploaded(row.path, row.customName);
    } else if (row.source === "app") {
      if (row.defaultUploadPath) {
        await downloadUploaded(row.defaultUploadPath, row.defaultUploadName || row.customName);
      } else if (row.appId != null) {
        await downloadTallyPdf(row.appId, row.customName);
      }
    }
  };
  const { isBusy: isDownloading, run: runDownload } = useBusyAction(handleDownload);

  // ---- Derived: well filter -> search -> source/owner/date filters ----
  const filteredByWell =
    selectedWellId != null ? rows.filter((r) => r.wellId === selectedWellId) : rows;

  let filtered = docSearch.trim()
    ? filteredByWell.filter((r) =>
        r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()),
      )
    : filteredByWell;
  if (source) filtered = filtered.filter((r) => r.source === source);
  if (owner) filtered = filtered.filter((r) => r.owner === owner);
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      const d = new Date(r.date + "T12:00:00");
      if (dateFrom && d < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }

  const ownerOptions = Array.from(new Set(rows.map((r) => r.owner))).filter(
    (o) => o && o !== "—",
  );

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Tally Design", render: (r) => r.customName, width: "2fr" },
    { key: "wellName", header: "Well", render: (r) => r.wellName || "—", width: "160px" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
    {
      key: "source",
      header: "Source",
      render: (r) => (r.source === "app" ? "App Design" : "Uploaded"),
      width: "110px",
    },
    { key: "owner", header: "Created by", render: (r) => r.owner, width: "160px" },
  ];

  const filters: ListFilterField[] = [
    {
      kind: "select",
      key: "source",
      label: "Source",
      value: source,
      onChange: setSource,
      options: [
        { value: "app", label: "App Design" },
        { value: "uploaded", label: "Uploaded" },
      ],
    },
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];
  if (ownerOptions.length > 0) {
    filters.splice(1, 0, {
      kind: "select",
      key: "owner",
      label: "Created by",
      value: owner,
      onChange: setOwner,
      options: ownerOptions.map((o) => ({ value: o, label: o })),
    });
  }

  return (
    <ListPageLayout<DocRow>
      title="Tally Designs"
      subtitle="Browse, filter and download client tally designs"
      columns={columns}
      rows={pageRows}
      getRowId={(r) => r.rowId}
      rowsLoading={rowsLoading}
      isError={isError}
      searchValue={docSearch}
      onSearchChange={setDocSearch}
      searchPlaceholder="Search tally designs…"
      filters={filters}
      page={currentPage}
      pageSize={paginationModel.pageSize}
      totalCount={totalCount}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      rowsPerPageOptions={[8, 15, 25, 50]}
      sidePanel={{
        title: "Wells",
        items: wells.map((w) => ({ id: w.id ?? -1, label: w.name })),
        selectedId: selectedWellId,
        onSelect: (id) => {
          setSelectedWellId(id as number | null);
          setPaginationModel({ ...paginationModel, page: 0 });
        },
        loading: wellsLoading,
        searchPlaceholder: "Search wells…",
      }}
      toolbarActions={
        showClientFilter && clientOptions?.length ? (
          <select
            value={selectedClientId ? String(selectedClientId) : ""}
            onChange={(e) => {
              const c =
                clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
              setSelectedClientId(c?.id ?? null);
            }}
          >
            <option value="" disabled>Select a client...</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        ) : undefined
      }
      rowActions={(r) => (
        <DownloadAction rowId={r.rowId} busy={isDownloading} onClick={() => runDownload(r)} />
      )}
    />
  );
};
